import { useCallback, useEffect, useState } from "react";

import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { IPharmacyInfo } from "@/Common/Api/Pharmacy/Dto/Responses/IPharmacyInfo";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { LinkingPopupService } from "@/Common/Components/Modals/LinkingPopup/LinkingPopupService";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { LoginPinFrequencyModel } from "@/Common/Models/Employee/LoginFrequencyModel";
import { Guid } from "@/Common/Models/Guid";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { DirectoryPharmacy } from "@/ContactDirectory/Models/DirectoryPharmacy";
import { CustomFaxClient } from "@/ExportableFile/Api/CustomFaxClient";
import { FaxSignPrintExportableFileComponent } from "@/ExportableFile/Components/FaxSignPrintExportableFileComponent";
import { IFaxCoverSheetProps } from "@/ExportableFile/Components/IFaxInformationProps";
import { ExportMode } from "@/ExportableFile/Models/enum/ExportMode";
import { ExportableFile } from "@/ExportableFile/Models/ExportableFile";
import { ExportableFilePopupSettingModel } from "@/ExportableFile/Models/ExportableFilePopupSettingModel";
import { ExportableFilePopupSignatureSettingsModel } from "@/ExportableFile/Models/ExportableFilePopupSignatureSettingsModel";
import { OverlayElementType } from "@/ExportableFile/Models/OverlayElements/OverlayElement";
import { PrepositionedPharmacyInfoOverlayElement } from "@/ExportableFile/Models/OverlayElements/PrepositionedPharmacyInfoOverlayElement";
import { faxPrescriptionRenewal, setPreferredPharmacy, signoffEncounter } from "../Api/PrescriptionRenewalsClient";
import { PrescriptionRenewalCurrentDocument } from "../Models/PrescriptionRenewalCurrentDocument";

interface PrescriptionRenewalExportableFileFaxPopupProps {
    patientContactId: Guid;
    documentId: Guid;
    exportableFile: ExportableFile;
    encounterId: Guid;
    exportMode: ExportMode;
    preferredPharmacy: IPharmacyInfo;
    attachedDocument: PrescriptionRenewalCurrentDocument;
    onNextPrescriptionRenewal(): void;
    onToggleFaxAndPrintPopup(): void;
    onPrint?(): void | Promise<void>;
}

function PrescriptionRenewalExportableFileFaxPopup(props: PrescriptionRenewalExportableFileFaxPopupProps): JSX.Element {
    const { getText } = useTranslation();
    const [faxInfo, setFaxInfo] = useState<PhoneNumber>(PhoneNumber.newPhone(ContactPointType.Work, PhoneType.Fax));
    const [faxCoverSheet, setFaxCoverSheet] = useState<IFaxCoverSheetProps>();
    const [faxDescription, setFaxDescription] = useState<string>("");
    const [recipientPhoneNumber, setRecipientPhoneNumber] = useState<string>("");
    const [documentFaxSource, setDocumentFaxSource] = useState<IPharmacyInfo>(null);
    const [mylePharmacy, setMylePharmacy] = useState<DirectoryPharmacy>(null);
    const [isSignatureValid, setIsSignatureValid] = useState<boolean>(false);
    const [signedExportableFile, setSignedExportableFile] = useState<ExportableFile>(null);
    const [shouldSaveSignature, setShouldSaveSignature] = useState<boolean>(false);
    const [attachOrginalDocument, setAttachOrginalDocument] = useState<boolean>(false);
    const [hasPinSet, setHasPinSet] = useState<boolean>(sessionStore.hasPinSet);
    const [hasSignOnPrintAccess, setHasSignOnPrintAccess] = useState<boolean>(sessionStore.signOnPrintAccess);
    const [loginPinFrequencyModel, setLoginPinFrequencyModel] = useState<LoginPinFrequencyModel>(() => LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model?.LoginPinFrequency));
    const [exportableFilePopupSetting, setExportableFilePopupSetting] = useState<ExportableFilePopupSettingModel>(ExportableFilePopupSettingModel.fromCacheModel(cacheStore.UserSettings.ExportableFilePopup) ?? new ExportableFilePopupSettingModel());

    useEffect(() => {
        const fetchFaxSenderInformation = async (documentId: Guid): Promise<void> => {
            const faxSourceData = await CustomFaxClient.fetchFaxSenderData(documentId);

            const sourcePharmacy: IPharmacyInfo = { Fax: getFormattedFaxNumber(faxSourceData.SenderFaxNumber), Banner: faxSourceData.Source, Name: "", Phone: "", Address: "" };
            setDocumentFaxSource(sourcePharmacy);
        };

        fetchFaxSenderInformation(props.documentId);
    }, [props.documentId]);

    const onSignPrescriptionRenewal = (exportableFile: ExportableFile): void => {
        setIsSignatureValid(true);
        setSignedExportableFile(exportableFile);
    };

    const onPhoneNumberChanged = (faxNumber: string, isFaxNumberValid: boolean, mylePharmacy: DirectoryPharmacy, faxDescription: string, phoneNumber: string): void => {
        const newPhone: PhoneNumber = PhoneNumber.newPhone(ContactPointType.Work, PhoneType.Fax);
        newPhone.Number = faxNumber;
        newPhone.IsValid = isFaxNumberValid;
        setFaxInfo(newPhone);
        setRecipientPhoneNumber(phoneNumber);
        setFaxDescription(faxDescription);
        setMylePharmacy(mylePharmacy);
    };

    const redirectToPatientTabAndOpenEncounter = (encounterId: Guid | string): void => {
        patientStore.setActivePatientUI(props.patientContactId, true, () => {
            patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                props.patientContactId,
                null,
                true,
                true,
                "PrescriptionRenewalExportableFileFaxPopup_RedirectToPatientEncounter",
                () => {
                    patientStore.ActionsMedicalNote.LoadEncounterForEdit(encounterId?.toString());
                },
                false
            );
        }, true, false);
    };

    const onFaxCoverSheetChange = useCallback((update: IFaxCoverSheetProps) => {
        setFaxCoverSheet(prev => ({
            ...prev,
            ...update
        }));
    }, []);

    const faxPrescriptionRenewals = async (): Promise<void> => {
        const unsignedExportableFileDto = await props.exportableFile?.toFileDto(true, true, ExportMode.Fax);
        const signedExportableFileDto = await signedExportableFile?.toFileDto(true, true, ExportMode.Fax);
        const attachedOrginalDocument = attachOrginalDocument ? props.attachedDocument : null;

        const response = await faxPrescriptionRenewal(
            props.patientContactId,
            unsignedExportableFileDto,
            signedExportableFileDto,
            faxInfo.Number,
            faxDescription,
            attachedOrginalDocument,
            mylePharmacy,
            faxCoverSheet,
            recipientPhoneNumber
        );

        const faxInfoOverlay = signedExportableFile?.overlayElements?.firstOrDefault(p => p.overlayElementType === OverlayElementType.PrepositionedPharmacyInfo) as PrepositionedPharmacyInfoOverlayElement;
        if (faxInfoOverlay?.Pharmacy && faxInfoOverlay?.IsSetPreferred) {
            const pharmacy = faxInfoOverlay.Pharmacy;
            await setPreferredPharmacy(props.patientContactId, pharmacy);
        }

        if (!response.isSuccess) {
            sessionStore.showPopup({
                title: getText("ErrorMLabel"),
                content: getText("FaxFailedToSend")
            });
        }
    };

    const updateExportableFilePopupSettings = (currentAction: ExportMode): void => {
        const updatedSignatureSettings = [
            ...(exportableFilePopupSetting.prescription.exportableFileSignatureSettings ?? [])
                .filter(p => p.actionType !== currentAction),
            {
                actionType: currentAction,
                shouldSign: !!signedExportableFile?.hasSignature()
            }
        ];
        const updatedSettings = {
            ...exportableFilePopupSetting,
            prescription: {
                ...exportableFilePopupSetting.prescription,
                exportableFileSignatureSettings: updatedSignatureSettings
            }
        };

        sessionStore.dispatch(LocalPatientOperations.MedicalNote.UpdateUserSettingsExportableFilePopup, {
            settings: updatedSettings
        });
    };

    const actionsBeforeRedirect = async (openNote: boolean = false, printFile: boolean = false, faxFile: boolean = false, signFile: boolean = false): Promise<void> => {
        if (faxFile) {
            await faxPrescriptionRenewals();
        }

        const persistSignature = cacheStore.ClinicSettings.EnableQuickDocumentSigning ? exportableFilePopupSetting.prescription.isSaveSignature : shouldSaveSignature;
        if ((persistSignature || signFile) && signedExportableFile) {
            await signedExportableFile.persistSignature();
        }

        if (printFile) {
            const fileToPrint = isSignatureValid ? signedExportableFile : props.exportableFile;

            await fileToPrint?.toFileDto(true, true, ExportMode.Print);

            fileToPrint?.print();

            props.onPrint?.();
        }

        props.onToggleFaxAndPrintPopup();

        if (openNote) {
            redirectToPatientTabAndOpenEncounter(props.encounterId);
        }
        else {
            if (!IsNullOrUndefined(props.encounterId)) {
                LinkingPopupService
                    .promptForAdditionalSignOffInformation(
                        props.patientContactId,
                        sessionStore.getCurrentEmployee().getActiveSite().Model.Id,
                        null,
                        null
                    )
                    .then(async info => {
                        await signoffEncounter(props.encounterId, info);
                        patientStore.ActionsMedicalSummary.LoadPatientCumulativeProfile(
                            props.patientContactId,
                            null,
                            true,
                            true,
                            "PrescriptionRenewalExportableFileFaxPopup_SignOffEncounter",
                            null,
                            true
                        );
                    })
                    .catch(p => p && console.error(p)); // prompt cancelled
            }
            props.onNextPrescriptionRenewal();
        }

        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
            let currentAction = ExportMode.Print;
            if (faxFile)
                currentAction = ExportMode.Fax;
            else if (signFile)
                currentAction = ExportMode.Sign;

            updateExportableFilePopupSettings(currentAction);
        }
    };

    const saveSignatureChanged = (): void => {
        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
            const prescriptionSetting: ExportableFilePopupSignatureSettingsModel = exportableFilePopupSetting.prescription;
            prescriptionSetting.isSaveSignature = !prescriptionSetting.isSaveSignature;
            setExportableFilePopupSetting(prev => ({ ...prev, prescription: prescriptionSetting }));
        }

        setShouldSaveSignature(!shouldSaveSignature);
    };

    const onChangeAttachOrginalDocument = (checked: boolean): void => {
        if (cacheStore.ClinicSettings.EnableQuickDocumentSigning) {
            const prescriptionSetting: ExportableFilePopupSignatureSettingsModel = exportableFilePopupSetting.prescription;
            prescriptionSetting.isAttachOriginalDocument = !prescriptionSetting.isAttachOriginalDocument;
            setExportableFilePopupSetting(prev => ({ ...prev, prescription: prescriptionSetting }));
        }

        setAttachOrginalDocument(checked);
    };

    const getFormattedFaxNumber = (faxNumber: string = ""): string => faxNumber.replace(/^(\+1)?|[-\s]/g, "");

    const canSignPrintPrescription: boolean = sessionStore.canSignPrescription(sessionStore.getCurrentEmployee().ContactFacetId);
    const needsSignatureValidation: boolean = !isSignatureValid && !cacheStore.ClinicSettings.EnableQuickDocumentSigning;
    const isFaxButtonDisabled = (): boolean => !sessionStore.faxAccess || !canSignPrintPrescription || !faxInfo?.IsValid || needsSignatureValidation;

    const faxSignPrintButtons = (shouldDisableFaxButton: boolean): JSX.Element => {
        const disableFaxButton = shouldDisableFaxButton || isFaxButtonDisabled();
        switch (props.exportMode) {
            case ExportMode.Fax:
                return (
                    <>
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("FaxOpenNoteLabel")}
                            disabled={disableFaxButton || IsNullOrUndefined(props.encounterId)}
                            onClick={async () => await actionsBeforeRedirect(true, false, true)}
                            dataCy="Fax-Open-Note-button"
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("FaxVerbLabel")}
                            disabled={disableFaxButton}
                            onClick={async () => await actionsBeforeRedirect(false, false, true)}
                            dataCy="Fax-button"
                        />
                    </>
                );
            case ExportMode.Print:
                return (
                    <>
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("PrintOpenNoteLabel")}
                            disabled={!canSignPrintPrescription || IsNullOrUndefined(props.encounterId) || needsSignatureValidation}
                            onClick={async () => await actionsBeforeRedirect(true, true)}
                            dataCy="Print-Open-Note-button"
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("PrintLabel")}
                            disabled={!canSignPrintPrescription || needsSignatureValidation}
                            onClick={async () => await actionsBeforeRedirect(false, true)}
                            dataCy="Print-button"
                        />
                    </>
                );
            case ExportMode.Sign:
                return (
                    <>
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("SignOpenNoteLabel")}
                            disabled={!canSignPrintPrescription || IsNullOrUndefined(props.encounterId) || !signedExportableFile?.hasSignature() || needsSignatureValidation}
                            onClick={async () => await actionsBeforeRedirect(true, false, false, true)}
                            dataCy="Sign-Open-Note-button"
                        />
                        <ButtonControl
                            className="btn btn-primary"
                            label={getText("SignLabel")}
                            disabled={!canSignPrintPrescription || !signedExportableFile?.hasSignature() || needsSignatureValidation}
                            onClick={async () => await actionsBeforeRedirect(false, false, false, true)}
                            dataCy="Sign-button"
                        />
                    </>
                );
        }
    };

    function changeListener(): void {
        setHasSignOnPrintAccess(sessionStore.signOnPrintAccess);
        setHasPinSet(sessionStore.hasPinSet);
        setLoginPinFrequencyModel(LoginPinFrequencyModel.fromResponseModel(sessionStore.getCurrentEmployee()?.Model.LoginPinFrequency));
    }

    useEffect(
        () => {
            const sessionStorechangeListenerId = sessionStore.AddChangeListener(() => changeListener(), null);
            return () => sessionStore.RemoveChangeListener(sessionStorechangeListenerId);
        },
        []
    );

    return (
        <>
            {!!documentFaxSource && (
                <FaxSignPrintExportableFileComponent
                    exportableFile={props.exportableFile}
                    mode={props.exportMode}
                    patientContactId={props.patientContactId}
                    onClose={() => {
                        props.onNextPrescriptionRenewal();
                        props.onToggleFaxAndPrintPopup();
                        setFaxCoverSheet({
                            ...faxCoverSheet,
                            ...{
                                CoverPageTitle: null,
                                CoverPageContent: null
                            }
                        });
                    }}
                    onValidateSignatureCallback={exportableFile => onSignPrescriptionRenewal(exportableFile)}
                    disableSaveSignature={true}
                    customCheckBox={props.exportMode !== ExportMode.Sign && (
                        <>
                            <CheckboxControl
                                checked={cacheStore.ClinicSettings.EnableQuickDocumentSigning ? exportableFilePopupSetting.prescription.isSaveSignature : shouldSaveSignature}
                                label={getText("SaveSignatureLabel")}
                                onChange={() => saveSignatureChanged()}
                                disabled={!isSignatureValid}
                            />
                            {props.exportMode === ExportMode.Fax && (
                                <CheckboxControl
                                    checked={cacheStore.ClinicSettings.EnableQuickDocumentSigning ? exportableFilePopupSetting.prescription.isAttachOriginalDocument : attachOrginalDocument}
                                    label={getText("AttachOriginalDocumentLabel")}
                                    onChange={checked => onChangeAttachOrginalDocument(checked)}
                                />
                            )}
                        </>
                    )}
                    defaultContext={props.preferredPharmacy}
                    customPharmacy={documentFaxSource}
                    renderCustomButtons={(shouldDisableFaxButton: boolean) => (
                        <div className="flex-container flex-col-gap-xs">
                            <ButtonControl
                                className="btn btn-default"
                                label={getText("CancelLabel")}
                                onClick={() => {
                                    props.onToggleFaxAndPrintPopup();
                                    props.onNextPrescriptionRenewal();
                                }}
                                dataCy="cancel-button"
                            />
                            {faxSignPrintButtons(shouldDisableFaxButton)}
                        </div>
                    )}
                    onCustomFax={(senderFaxNumber, isFaxNumberValid, mylePharmacy, faxDescription, phoneNumber) => onPhoneNumberChanged(senderFaxNumber, isFaxNumberValid, mylePharmacy, faxDescription, phoneNumber)}
                    hasPinSet={hasPinSet}
                    hasSignOnPrintAccess={hasSignOnPrintAccess}
                    loginPinFrequencyModel={loginPinFrequencyModel}
                    onFaxCoverSheetChange={onFaxCoverSheetChange}
                    faxCoverSheet={faxCoverSheet}
                />
            )}
        </>
    );
}

export { PrescriptionRenewalExportableFileFaxPopup };
